import React, { useState } from 'react';
import { Button } from '@headlessui/react';
import { 
  FaPlus, FaHeadset, FaExternalLinkAlt, FaQuestionCircle
} from 'react-icons/fa';
import SupportModuleCard from './SupportModuleCard';
import SupportModuleForm from './SupportModuleForm';
import SupportModuleHowTo from './SupportModuleHowTo';
import UpgradeModal from './UpgradeModal';
import VideoModal from './VideoModal';

const SupportModulesSection = ({ 
  user, 
  supportModules, 
  projects, 
  onStatsUpdate, 
  onShowToast 
}) => {
  const [showSupportModuleForm, setShowSupportModuleForm] = useState(false);
  const [editingSupportModule, setEditingSupportModule] = useState(null);
  const [showSupportModuleHowTo, setShowSupportModuleHowTo] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalSection, setUpgradeModalSection] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user can create more support modules
  const canCreateSupportModule = () => {
    const userPlan = user?.subscription?.plan || 'free';
    const currentCount = supportModules.length;
    const userLimit = user?.subscription?.maxSupportModules;
    
    // Enterprise planında sınırsız
    if (userPlan === 'enterprise' || userLimit === -1) {
      return true;
    }
    
    // Kullanıcının limitini kullan, yoksa fallback
    if (userLimit) {
      return currentCount < userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return currentCount < 1;
      case 'premium':
        return currentCount < 5;
      default:
        return currentCount < 1;
    }
  };

  // Handle upgrade click
  const handleUpgradeClick = (section = null) => {
    setUpgradeModalSection(section);
    setShowUpgradeModal(true);
  };

  const handleUpgradeSuccess = () => {
    setShowUpgradeModal(false);
    onShowToast('Subscription upgraded successfully! You can now create more support modules.', 'success');
    if (onStatsUpdate) {
      onStatsUpdate();
    }
  };

  const handleCreateSupportModule = async (supportModuleData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/support/user/support-modules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(supportModuleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.message && errorData.message.includes('limitinize ulaştınız')) {
          handleUpgradeClick('support');
          return;
        }
        throw new Error(errorData.message || 'Failed to create support module');
      }

      const result = await response.json();
      setShowSupportModuleForm(false);
      setEditingSupportModule(null);
      if (onStatsUpdate) {
        onStatsUpdate();
      }
      onShowToast('Support module created successfully!', 'success');
    } catch (error) {
      console.error('Create support module error:', error);
      if (error.message !== 'Authentication failed') {
        if (error.message && error.message.includes('limitinize ulaştınız')) {
          handleUpgradeClick('support');
        } else {
          onShowToast(`Error occurred while creating support module: ${error.message}`, 'error');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSupportModule = async (supportModuleId, supportModuleData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/support/user/support-modules/${supportModuleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(supportModuleData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update support module');
      }

      const result = await response.json();
      setShowSupportModuleForm(false);
      setEditingSupportModule(null);
      onShowToast('Support module updated successfully!', 'success');
    } catch (error) {
      console.error('Update support module error:', error);
      if (error.message !== 'Authentication failed') {
        onShowToast(`Error occurred while updating support module: ${error.message}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotificationEmailUpdate = async (supportModuleId, notificationEmail) => {
    const response = await fetch(`/api/support/user/support-modules/${supportModuleId}/notification-email`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ notificationEmail })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || 'Could not update notification email.');
    if (onStatsUpdate) await onStatsUpdate();
    onShowToast('Notification email updated successfully.', 'success');
  };

  const handleDeleteSupportModule = async (supportModule) => {
    if (!window.confirm('Are you sure you want to delete this support module?')) return;

    try {
      const supportModuleId = supportModule._id || supportModule;
      const response = await fetch(`/api/support/user/support-modules/${supportModuleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        if (onStatsUpdate) {
          onStatsUpdate();
        }
        onShowToast('Support module deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        onShowToast(errorData.message || 'Failed to delete support module', 'error');
      }
    } catch (error) {
      console.error('Delete support module error:', error);
      if (error.message !== 'Authentication failed') {
        onShowToast(`Error occurred while deleting support module: ${error.message}`, 'error');
      }
    }
  };

  const handleEditSupportModule = (supportModule) => {
    setEditingSupportModule(supportModule);
    setShowSupportModuleForm(true);
  };

  return (
    <div className="bg-black rounded-2xl border border-gray-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-white">Support Modules</h3>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => setShowVideoModal(true)}
            className="group flex items-center px-4 py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white"
          >
            <FaQuestionCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
            How to Create?
          </Button>
          <Button
            onClick={() => setShowSupportModuleHowTo(true)}
            className="group flex items-center px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border border-gray-700"
          >
            <FaExternalLinkAlt className="mr-2 h-4 w-4" />
            How it Works
          </Button>
          <Button
            onClick={() => {
              if (!canCreateSupportModule()) {
                handleUpgradeClick('support');
                return;
              }
              setEditingSupportModule(null);
              setShowSupportModuleForm(true);
            }}
            disabled={projects.filter(project => project.status === 'active').length === 0}
            className={`group flex items-center px-6 py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border disabled:opacity-50 disabled:cursor-not-allowed ${
              canCreateSupportModule() 
                ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
            }`}
          >
            <FaPlus className={`mr-2 transition-transform duration-200 ${
              canCreateSupportModule() ? 'group-hover:rotate-90' : 'animate-bounce'
            }`} />
            {canCreateSupportModule() ? 'New Support Module' : 'Upgrade to Create More'}
          </Button>
        </div>
      </div>

      {supportModules.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto h-12 w-12 bg-gray-900 rounded-full flex items-center justify-center mb-4">
            <FaHeadset className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No support modules yet</h3>
          <p className="text-gray-400 mb-6">
            {projects.filter(project => project.status === 'active').length === 0 
              ? 'You need to have at least one active project to create a support module.'
              : 'Create a support module for your active project to start managing support tickets.'
            }
          </p>
          <div className="flex items-center justify-center space-x-3">
            <Button
              onClick={() => setShowVideoModal(true)}
              className="group flex items-center px-4 py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white"
            >
              <FaQuestionCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
              How to Create?
            </Button>
            <Button
              onClick={() => {
                if (!canCreateSupportModule()) {
                  handleUpgradeClick('support');
                  return;
                }
                setEditingSupportModule(null);
                setShowSupportModuleForm(true);
              }}
              disabled={projects.filter(project => project.status === 'active').length === 0}
              className={`inline-flex items-center px-6 py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-100 data-focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed ${
                canCreateSupportModule() 
                  ? 'bg-white text-black hover:bg-gray-100' 
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 animate-pulse'
              }`}
            >
              <FaPlus className={`mr-2 transition-transform duration-200 ${
                canCreateSupportModule() ? '' : 'animate-bounce'
              }`} />
              {canCreateSupportModule() ? 'Create Support Module' : 'Upgrade to Create More'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {supportModules.map((supportModule) => (
            <SupportModuleCard
              key={supportModule._id}
              supportModule={supportModule}
              onDelete={handleDeleteSupportModule}
              onNotificationEmailUpdate={handleNotificationEmailUpdate}
            />
          ))}
        </div>
      )}

      {/* Support Module Form Modal */}
      <SupportModuleForm
        isOpen={showSupportModuleForm}
        onClose={() => {
          setShowSupportModuleForm(false);
          setEditingSupportModule(null);
        }}
        onSubmit={editingSupportModule ? (data) => handleUpdateSupportModule(editingSupportModule._id, data) : handleCreateSupportModule}
        supportModule={editingSupportModule}
        projects={projects}
        isLoading={isSubmitting}
      />

      {/* Support Module How To Modal */}
      <SupportModuleHowTo
        isOpen={showSupportModuleHowTo}
        onClose={() => setShowSupportModuleHowTo(false)}
        projects={projects}
        supportModules={supportModules}
        onShowToast={onShowToast}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
        onUpgrade={handleUpgradeSuccess}
        highlightSection={upgradeModalSection}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl="https://www.youtube.com/embed/TZDdDymeQ04"
        title="How to Create a Support Module?"
      />
    </div>
  );
};

export default SupportModulesSection;
